import { useState, useEffect } from "react";
import { useStudyPlan } from "../../contexts/study.context";

/**
 * Custom hook to handle the warning dialog functionality for sites to activate
 * @param {Object} sites_to_activate - The sites to activate parameter object
 * @param {string} boundProperty - Either "upperBound" or "lowerBound"
 * @param {Function} handleUpdateSiteGroupParameters - Function to update site group parameters
 * @returns {Object} - Object containing all the necessary state and handlers
 */
export const useActivationProfilesWarning = (
  sites_to_activate,
  boundProperty,
  handleUpdateSiteGroupParameters,
) => {
  const {
    isEditingAPTableEnabled,
    savePlan,
  } = useStudyPlan();

  const [showWarning, setShowWarning] = useState(false);
  const [originalValue, setOriginalValue] = useState(null);
  const [tempBoundValue, setTempBoundValue] = useState(sites_to_activate[boundProperty]);

  const isEmpty = (value) => value === "" || value === null || value === undefined;

  // Sync tempBoundValue when the actual value changes (e.g., when switching countries)
  useEffect(() => {
    setTempBoundValue(sites_to_activate[boundProperty]);
  }, [sites_to_activate, boundProperty]);

  const handleBlur = (value) => {
    // Convert value to number or null
    const numericValue = value !== "" && value !== null && value !== undefined ? Number(value) : null;
    
    // Check if the value actually changed from the original
    if (numericValue !== originalValue) {
      if (!isEditingAPTableEnabled) {
        // Show warning dialog for changes
        setShowWarning(true);
      } else {
        // For Remodel types, apply changes directly without warning
        const currentValue = tempBoundValue !== null ? tempBoundValue : sites_to_activate[boundProperty];
        handleUpdateSiteGroupParameters(sites_to_activate, boundProperty, currentValue);
      }
    } else if (isEmpty(value)) {
      // If field is cleared, revert to original value
      setTempBoundValue(originalValue);
    }
  };

  const handleFocus = () => {
    // Store the original value when user starts editing
    setOriginalValue(sites_to_activate[boundProperty]);
  };

const handleWarningConfirm = async () => {
    const currentValue = tempBoundValue !== null ? tempBoundValue : sites_to_activate[boundProperty];
    handleUpdateSiteGroupParameters(sites_to_activate, boundProperty, currentValue);
    setShowWarning(false);
    setOriginalValue(null);
    
    try {
      await savePlan({ isNew: false });
    } catch (error) {
      console.error("Error saving plan:", error);
    }
  };

  const handleWarningCancel = () => {
    // User cancelled - revert to original value
    setTempBoundValue(originalValue);
    setShowWarning(false);
    setOriginalValue(null);
  };

  return {
    showWarning,
    tempBoundValue,
    setTempBoundValue,
    handleBlur,
    handleFocus,
    handleWarningConfirm,
    handleWarningCancel,
  };
};
